import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Heart, Search, ShoppingCart, User, X } from "lucide-react";
import { useAuth, useCart, useSite } from "../lib/store";
import { media } from "../lib/api";
import { CatIcon, Daisy, Logo } from "./Daisy";

const NAV = [
  { to: "/", label: "Domů", id: "home" },
  { to: "/eshop", label: "E-shop", id: "eshop" },
  { to: "/darkove-poukazy", label: "Dárkové poukazy", id: "gifts" },
  { to: "/o-nas", label: "O nás", id: "about" },
  { to: "/kontakt", label: "Kontakt", id: "contact" },
];

const CategoriesMenu = ({ cats }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="link-underline flex items-center gap-1 text-[15px] font-medium" onClick={() => setOpen((o) => !o)} data-testid="nav-categories-dropdown">
        Kategorie <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }}
            className="absolute left-1/2 top-full z-50 mt-4 w-[440px] -translate-x-1/2 rounded-3xl border bg-[#FFFDF8] p-3 shadow-xl">
            <div className="grid grid-cols-2 gap-1">
              {cats.map((c) => (
                <Link key={c.id} to={`/kategorie/${c.slug}`} onClick={() => setOpen(false)} data-testid={`nav-category-${c.slug}`}
                  className="group flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-[#E9EFE0]">
                  {c.image ? <img src={media(c.image)} alt="" className="h-11 w-11 rounded-xl object-cover" /> : null}
                  <span className="flex-1 font-medium">{c.name}</span>
                  <CatIcon name={c.icon} className="h-4 w-4 text-[#7A8460] transition-transform group-hover:rotate-12" />
                </Link>
              ))}
            </div>
            <Link to="/eshop" onClick={() => setOpen(false)} className="mt-2 block rounded-2xl bg-[#A3AC86] py-2 text-center text-sm font-semibold text-white">Zobrazit vše</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const IconBtn = ({ children, badge, testid, ...rest }) => (
  <button {...rest} data-testid={testid} className="relative grid h-10 w-10 place-items-center rounded-full text-[#2D3426] transition-colors hover:bg-[#E9EFE0]">
    {children}
    {badge > 0 && (
      <motion.span key={badge} initial={{ scale: 0.3 }} animate={{ scale: 1 }} className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#7A8460] px-1 text-[10px] font-bold text-white" data-testid={`${testid}-count`}>
        {badge}
      </motion.span>
    )}
  </button>
);

export const Header = () => {
  const { site } = useSite();
  const { user } = useAuth();
  const { count, setOpen, favs } = useCart();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const cats = site?.categories || [];
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  const search = (e) => {
    e.preventDefault();
    nav(`/eshop?q=${encodeURIComponent(q)}`);
    setMobile(false);
  };
  return (
    <header className={`sticky top-0 z-40 border-b transition-all duration-300 ${scrolled ? "bg-[#F7F3EA]/90 py-2 shadow-sm backdrop-blur-xl" : "bg-[#F7F3EA] py-3 border-transparent"}`} data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 sm:gap-6 sm:px-8">
        <Logo />
        <nav className="ml-6 hidden items-center gap-8 lg:flex">
          {NAV.slice(0, 2).map((n) => (
            <NavLink key={n.to} to={n.to} end className={({ isActive }) => `link-underline text-[15px] font-medium ${isActive ? "active" : ""}`} data-testid={`nav-${n.id}-link`}>{n.label}</NavLink>
          ))}
          <CategoriesMenu cats={cats} />
          {NAV.slice(2).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `link-underline text-[15px] font-medium ${isActive ? "active" : ""}`} data-testid={`nav-${n.id}-link`}>{n.label}</NavLink>
          ))}
        </nav>
        <form onSubmit={search} className="ml-auto hidden items-center gap-2 rounded-full border bg-[#EFEBE1] px-4 py-2 transition-shadow focus-within:ring-2 focus-within:ring-[#A3AC86] md:flex">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat..." className="w-36 bg-transparent text-sm outline-none xl:w-44" data-testid="header-search-input" />
          <button type="submit" data-testid="header-search-btn"><Search className="h-4 w-4" /></button>
        </form>
        <div className="ml-auto flex items-center gap-0 sm:gap-1 md:ml-0">
          <IconBtn testid="header-account-btn" onClick={() => nav(user ? "/ucet" : "/prihlaseni")} aria-label="Účet">
            {user?.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> : <User className="h-5 w-5" strokeWidth={1.6} />}
          </IconBtn>
          <span className="hidden sm:block"><IconBtn testid="header-favs-btn" badge={favs.length} onClick={() => nav("/oblibene")} aria-label="Oblíbené"><Heart className="h-5 w-5" strokeWidth={1.6} /></IconBtn></span>
          <IconBtn testid="cart-drawer-trigger" badge={count} onClick={() => setOpen(true)} aria-label="Košík"><ShoppingCart className="h-5 w-5" strokeWidth={1.6} /></IconBtn>
          <motion.button whileTap={{ scale: 0.85, rotate: 90 }} className="ml-1 grid h-11 w-11 place-items-center rounded-full bg-[#A3AC86] shadow-md lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn" aria-label="Menu">
            <motion.span animate={{ rotate: mobile ? 180 : 0 }} transition={{ type: "spring", damping: 12 }}><Daisy size={32} /></motion.span>
          </motion.button>
        </div>
      </div>
      {createPortal(<AnimatePresence>
        {mobile && (
          <motion.div className="fixed inset-0 z-50 bg-[#2D3426]/40 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobile(false)}>
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 24 }} onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm overflow-y-auto bg-[#F7F3EA] p-6" data-testid="mobile-menu">
              <button className="mb-6 ml-auto grid h-10 w-10 place-items-center rounded-full bg-white" onClick={() => setMobile(false)} data-testid="mobile-menu-close"><X /></button>
              <form onSubmit={search} className="mb-6 flex items-center gap-2 rounded-full border bg-white px-4 py-3">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat..." className="flex-1 bg-transparent text-sm outline-none" data-testid="mobile-search-input" />
                <Search className="h-4 w-4" />
              </form>
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} onClick={() => setMobile(false)} className="h-section block py-3 text-2xl" data-testid={`mobile-nav-${n.id}`}>{n.label}</Link>
                </motion.div>
              ))}
              <Link to="/oblibene" onClick={() => setMobile(false)} className="h-section block py-3 text-2xl" data-testid="mobile-nav-favs">Oblíbené ({favs.length})</Link>
              <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#6E7565]">Kategorie</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {cats.map((c) => (
                  <Link key={c.id} to={`/kategorie/${c.slug}`} onClick={() => setMobile(false)} className="flex items-center gap-2 rounded-2xl bg-white p-3 text-sm font-medium">
                    <CatIcon name={c.icon} className="h-4 w-4 text-[#7A8460]" /> {c.name}
                  </Link>
                ))}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
};
